import type {
  Feature,
  FeatureCollection,
  LineString,
  MultiLineString,
  MultiPoint,
  MultiPolygon,
  Point,
  Polygon,
} from 'geojson';

import type { CrsMetadata } from '../crs/model';

export type SourceId = string;

export type JsonPrimitive = boolean | number | string | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
export type FeatureProperties = Record<string, JsonValue> | null;

export type SupportedGeometry =
  Point | MultiPoint | LineString | MultiLineString | Polygon | MultiPolygon;

export type SupportedGeometryType = SupportedGeometry['type'];
export type VectorFeature = Feature<SupportedGeometry | null, FeatureProperties>;
export type VectorFeatureCollection = FeatureCollection<
  SupportedGeometry | null,
  FeatureProperties
>;

export type FieldValueType =
  'array' | 'boolean' | 'date' | 'integer' | 'mixed' | 'number' | 'object' | 'string' | 'unknown';

export type ScalarFieldValueType = 'boolean' | 'date' | 'integer' | 'number' | 'string';
export type TableScalarValue = boolean | number | string | null;

export interface FieldDefinition {
  readonly name: string;
  readonly type: FieldValueType;
  readonly nullable: boolean;
  readonly presentCount: number;
  readonly nullCount: number;
}

export interface GeometrySummary {
  readonly types: readonly SupportedGeometryType[];
  readonly counts: Readonly<Record<SupportedGeometryType, number>>;
  readonly isMixed: boolean;
  readonly renderableFeatureCount: number;
  readonly nullGeometryCount: number;
  readonly emptyGeometryCount: number;
}

/**
 * Longitude may be unwrapped beyond 180 degrees when that is the shortest
 * extent across the antimeridian. Source coordinates remain unchanged.
 */
export interface SpatialExtent {
  readonly west: number;
  readonly south: number;
  readonly east: number;
  readonly north: number;
  readonly crossesAntimeridian: boolean;
}

export interface SourceProvenance {
  readonly originalFileName: string;
  readonly originalFormat: 'csv' | 'geojson' | 'shapefile' | 'xlsx' | 'kml' | 'kmz' | 'gpkg';
  readonly datasetName?: string;
  readonly componentFileNames?: readonly string[];
}

export type SourceWarningCode =
  | 'coordinate-plausibility'
  | 'invalid-coordinate-rows'
  | 'no-renderable-geometry'
  | 'unsupported-kml-content';

export interface SourceWarning {
  readonly code: SourceWarningCode;
  readonly message: string;
}

interface SourceDescriptorBase {
  readonly id: SourceId;
  readonly name: string;
}

export interface VectorSourceDescriptor extends SourceDescriptorBase {
  readonly kind: 'vector';
  readonly crs: CrsMetadata;
  readonly data: VectorFeatureCollection;
  readonly provenance: SourceProvenance;
  readonly featureCount: number;
  readonly fields: readonly FieldDefinition[];
  readonly geometry: GeometrySummary;
  readonly extent: SpatialExtent | null;
  readonly warnings: readonly SourceWarning[];
}

export interface TableSourceDescriptor extends SourceDescriptorBase {
  readonly kind: 'table';
  readonly fields: readonly FieldDefinition[];
  /** Compact rows aligned to `fields`, in successful import order. */
  readonly rows: readonly (readonly TableScalarValue[])[];
  readonly rowCount: number;
  readonly provenance: {
    readonly originalFileName: string;
    readonly originalFormat: 'csv' | 'xlsx' | 'gpkg';
    readonly worksheetName?: string;
  };
  readonly warnings: readonly TableSourceWarning[];
}

export type TableSourceWarningCode = 'formula-without-result' | 'missing-cells';

export interface TableSourceWarning {
  readonly code: TableSourceWarningCode;
  readonly message: string;
}

export type SourceDescriptor = VectorSourceDescriptor | TableSourceDescriptor;

export function isVectorSource(source: SourceDescriptor): source is VectorSourceDescriptor {
  return source.kind === 'vector';
}

export function isTableSource(source: SourceDescriptor): source is TableSourceDescriptor {
  return source.kind === 'table';
}
